import { useContext, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import AuthLayout from "../../../components/auth/AuthLayout";
import { UserContext } from "../../../context/UserContext";
import PhoneStep from "./steps/PhoneStep";
import OtpStep from "./steps/OtpStep";
import ProfileStep from "./steps/ProfileStep";

const STEP_META = {
  phone: { title: "ورود یا ثبت‌نام" },
  otp: { title: "تأیید شماره موبایل" },
  profile: { title: "تکمیل ثبت‌نام" },
};

export default function Auth() {
  const navigate = useNavigate();
  const { user, loading, login } = useContext(UserContext);
  const reduceMotion = useReducedMotion();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);
  const [registrationToken, setRegistrationToken] = useState(null);
  const [expiredNotice, setExpiredNotice] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleAuthenticated = ({ token, user }) => { login(user, token); navigate("/dashboard", { replace: true }); };
  const handleSent = ({ phone: sentPhone, isNewUser: newUser }) => { setExpiredNotice(false); setPhone(sentPhone); setIsNewUser(newUser); setStep("otp"); };
  const handleVerified = (data) => {
    if (data.status === "authenticated") return handleAuthenticated(data);
    setRegistrationToken(data.registrationToken);
    setStep("profile");
  };
  const handleRegistrationExpired = () => { setRegistrationToken(null); setExpiredNotice(true); setStep("phone"); };
  const stepIndex = ["phone", "otp", "profile"].indexOf(step);

  // A "remembered" session is still restoring from localStorage — render
  // nothing rather than flashing the phone step before we know.
  if (loading) return null;
  // Already signed in (e.g. a "remember me" session, or navigating back to
  // this page by mistake) — go straight to the dashboard instead of asking
  // for the phone number again.
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <AuthLayout title={STEP_META[step].title}>
      <div className="mb-5" role="progressbar" aria-label={`مرحله ${stepIndex + 1} از 3`} aria-valuemin="1" aria-valuemax="3" aria-valuenow={stepIndex + 1}>
        <div className="flex items-center gap-2" aria-hidden="true">
          {[0, 1, 2].map((index) => <span key={index} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${index <= stepIndex ? "bg-primary" : "bg-gray-100"}`} />)}
        </div>
      </div>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={step} initial={reduceMotion ? false : { opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={reduceMotion ? {} : { opacity: 0, x: -14 }} transition={{ duration: 0.22, ease: "easeOut" }}>
          {step === "phone" && <>
            {expiredNotice && <p role="status" className="-mt-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-center text-xs text-amber-600">مهلت تکمیل ثبت‌نام به پایان رسید. لطفاً دوباره کد تأیید را دریافت کنید.</p>}
            <PhoneStep onSent={handleSent} />
          </>}
          {step === "otp" && <OtpStep phone={phone} isNewUser={isNewUser} rememberMe={rememberMe} onRememberMeChange={setRememberMe} onVerified={handleVerified} onEditPhone={() => setStep("phone")} />}
          {step === "profile" && <ProfileStep registrationToken={registrationToken} rememberMe={rememberMe} onCompleted={handleAuthenticated} onExpired={handleRegistrationExpired} />}
        </motion.div>
      </AnimatePresence>
    </AuthLayout>
  );
}
